import moment from 'moment';
import { API_DATE_FORMAT } from '../constants';

export const offsetToMinutes = (offset) => {
  if (!offset) return 0;
  const isNegative = offset.startsWith('-');
  const clean = isNegative ? offset.slice(1) : offset;
  const [h, m] = clean.split(':').map(Number);
  const total = h * 60 + m;
  return isNegative ? -total : total;
};

export const minutesToOffset = (totalMinutes) => {
  const abs = Math.abs(totalMinutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return totalMinutes < 0 ? `-${hh}:${mm}` : `${hh}:${mm}`;
};

//Getting first day of a week date as a key
export const getWeekKeyForDate = (date, firstDayOfWeek) => {
  const momentDate = moment(date);
  const firstDayMoment = firstDayOfWeek === 0 || firstDayOfWeek == null ? 1 : firstDayOfWeek - 1;
  const currentDay = momentDate.day();
  let daysToSubtract = currentDay - firstDayMoment;
  if (daysToSubtract < 0) {
    daysToSubtract += 7;
  }
  return momentDate.clone().subtract(daysToSubtract, 'days').format('YYYY-MM-DD');
};

export const getBranchIdByProjectId = (queryClient, projectsStoreKey, projectId) => {
  const data = queryClient.getQueryData(projectsStoreKey, { exact: true });
  return data?.projects?.find?.(({ id }) => id === Number(projectId))?.branchId;
};

export const filterHolidaysByBranch = (holidays = [], branchId) =>
  branchId
    ? (holidays || []).filter(({ branchIds }) => !branchIds?.length || branchIds.includes(Number(branchId)))
    : holidays || [];

export const groupHolidays = (holidays = [], firstDayOfWeek = 0) => {
  const { holidaysByDate, maxHolidaysPerWeek } = (holidays || []).reduce(
    (acc, holiday) => {
      const startDate = moment(holiday.start);
      const endDate = moment(holiday.end);
      const diffDays = endDate.diff(startDate, 'days');

      for (let i = 0; i <= diffDays; i++) {
        const currentDate = startDate.clone().add(i, 'days');
        const dateString = currentDate.format('YYYY-MM-DD');

        if (!acc.holidaysByDate[dateString]) {
          acc.holidaysByDate[dateString] = [];
        }

        acc.holidaysByDate[dateString].push(holiday);

        const weekString = getWeekKeyForDate(currentDate, firstDayOfWeek);

        acc.maxHolidaysPerWeek[weekString] = Math.max(
          acc.maxHolidaysPerWeek[weekString] || 0,
          acc.holidaysByDate[dateString].length
        );
      }
      return acc;
    },
    { holidaysByDate: {}, maxHolidaysPerWeek: {} }
  );

  return { holidaysByDate, maxHolidaysPerWeek };
};

export const handleMutatedBookingData = ({ previousData, data }) => {
  const currPreviousData = previousData?.data;

  if (!currPreviousData) {
    return;
  }

  // JUST TO CHECK FOR GIVEN EMPLOYEE ID IF ALREADY EXISTS - FOR PERFORMANCE
  const newEmployeesSet = new Set((currPreviousData?.employees || []).map(({ id }) => id));
  // THIS WILL BE THE FINAL RESULT
  const newEmployeesData = [];

  // MAPPED EMPLOYEES - WILL ALSO CONTAIN THE FINAL RESULT
  const newMappedEmployeesData = { ...currPreviousData.mappedEmployees };

  const newMappedShiftData = {};
  const newShiftsEmployeesCount = {};

  (data || []).forEach((item) => {
    const { employees, mappedEmployees, shift } = item;

    // EMPLOYEES
    employees.forEach((currEmployee) => {
      // employee data already exists in project
      if (newEmployeesSet.has(currEmployee.id)) {
        return;
      }

      newEmployeesSet.add(currEmployee.id);
      newEmployeesData.push(currEmployee);
    });

    // MAPPED EMPLOYEES
    Object.entries(mappedEmployees).reduce((output, [key, value]) => {
      // employee data already exists in project
      if (output[key]) {
        return output;
      }

      output[key] = value;
      return output;
    }, newMappedEmployeesData);

    //SHIFTS
    const { id: shiftId, stats, addedSlots, confirmations } = shift || {};
    newMappedShiftData[shiftId] = { stats, addedSlots, confirmations };
    newShiftsEmployeesCount[shiftId] =
      (previousData?.data?.project?.shiftsEmployeesCount?.[shiftId] || 0) + addedSlots.length;
  });

  const newData = {
    ...previousData.data,
    employees: (previousData?.data?.employees || []).concat(newEmployeesData),
    mappedEmployees: newMappedEmployeesData,
    project: {
      ...previousData.data.project,

      shiftsEmployeesCount: {
        ...previousData.data.project.shiftsEmployeesCount,
        ...newShiftsEmployeesCount
      },

      shifts: previousData.data.project.shifts.map((shift) => {
        const newShiftData = newMappedShiftData[shift.id];

        if (!newShiftData) {
          return shift;
        }

        return {
          ...shift,
          slots: shift.slots.concat(newShiftData.addedSlots),
          stats: newShiftData.stats,
          confirmations: newShiftData.confirmations
        };
      })
    }
  };

  return newData;
};

export const handleMutatedUnBookingData = ({ previousData, slotId, shift: shiftData = {} }) => {
  if (!previousData?.data) {
    return;
  }

  const { id: shiftId, stats, confirmations = [], unbookings = [] } = shiftData;

  const newData = {
    ...previousData.data,
    unbookings: previousData.data.unbookings
      .filter(({ shiftId: curShiftId }) => curShiftId !== shiftId)
      .concat(unbookings),

    project: {
      ...previousData.data.project,
      shiftsEmployeesCount: {
        ...previousData.data.project.shiftsEmployeesCount,
        [shiftId]: previousData?.data?.project?.shiftsEmployeesCount?.[shiftId]
          ? previousData?.data?.project?.shiftsEmployeesCount?.[shiftId] - 1
          : 0
      },

      shifts: previousData.data.project.shifts?.map((shift) =>
        shift.id !== shiftId
          ? shift
          : {
              ...shift,

              //remove slot
              slots: shift.slots.filter((slot) => slot.id !== slotId),
              stats,
              confirmations
            }
      )
    }
  };

  return newData;
};

export const generateShiftName = (data, mappedJobTypes, mappedLabels, mappedLocations, mappedPtoTypes) =>
  data.isLeave
    ? data.leaveType || ''
    : data.isPto
    ? mappedPtoTypes?.[data.ptoType]?.label || ''
    : data.isJob
    ? mappedJobTypes?.[data.jobTypeId]?.label || ''
    : data.labelId
    ? mappedLabels?.[data.labelId]?.title || ''
    : data.name
    ? `${mappedLocations?.[data.locationId]?.name || ''} , ${data.name}`
    : mappedLocations?.[data.locationId]?.name || '';

export const getMainProjectDateLabel = (mainProject, shortDateFormat) => {
  const start = moment(mainProject.startDate, API_DATE_FORMAT);
  const end = mainProject.endDate ? moment(mainProject.endDate, API_DATE_FORMAT) : start;
  return start.isSame(end, 'day')
    ? start.format(shortDateFormat)
    : `${start.format(shortDateFormat)} - ${end.format(shortDateFormat)}`;
};

export const formatMainProjectLabel = (mainProject, shortDateFormat) =>
  `${mainProject.name}, ${getMainProjectDateLabel(mainProject, shortDateFormat)}`;

export const getSpecificProjectStoreKey = ({ projectId, isLite, withPayroll }) => [
  'projects',
  'specific',
  projectId,
  isLite,
  withPayroll
];
